import React from "react";
import { Text as RNText, TextProps as RNTextProps } from "react-native";
import { colors, typography } from "@/constants";

export type IconName =
  | "account_circle"
  | "add_circle"
  | "arrow_back"
  | "arrow_drop_down"
  | "arrow_drop_up"
  | "check"
  | "check_circle"
  | "close"
  | "delete"
  | "emergency"
  | "error"
  | "fact_check"
  | "fitness_center"
  | "flag"
  | "format_list_bulleted"
  | "graphic_eq"
  | "headset"
  | "health_and_safety"
  | "info"
  | "insights"
  | "keyboard_arrow_down"
  | "keyboard_arrow_up"
  | "label"
  | "lock"
  | "login"
  | "logout"
  | "mail"
  | "menu"
  | "mic"
  | "mic_off"
  | "more_vert"
  | "notes"
  | "pause_circle"
  | "play_circle"
  | "question_mark"
  | "radio_button_checked"
  | "radio_button_unchecked"
  | "send"
  | "settings"
  | "support"
  | "thumbs_up_down"
  | "volume_off"
  | "volume_up";

export type IconSize = keyof typeof typography.symbolSizes;

export interface IconProps extends RNTextProps {
  /** The icon name (ligature) - e.g. "settings", "menu" */
  name: IconName;
  /** Font weight variant - defaults to regular */
  variant?: "regular" | "medium" | "semiBold" | "bold";
  /** Icon size from typography.symbolSizes (small: 18, medium: 20, large: 24, xlarge: 28) - defaults to medium */
  size?: IconSize;
  /** Icon color - defaults to primary (textPrimary) */
  color?: "primary" | "secondary" | "tertiary";
}

/**
 * A styled Icon component using Material Symbols variable font.
 * Ensures icons match the visual weight of text variants.
 */
export default function Icon({
  name,
  variant = "regular",
  size = "medium",
  color = "primary",
  style,
  ...props
}: IconProps) {
  const fontFamily = typography.symbolFamily[variant];
  const fontSize = typography.symbolSizes[size];
  const textColor =
    color === "primary"
      ? colors.onSurface
      : color === "secondary"
        ? colors.onSurfaceVariant
        : colors.onSurfaceMuted;

  return (
    <RNText
      style={[
        {
          fontFamily,
          fontSize,
          color: textColor,
          includeFontPadding: false,
          textAlign: "center",
          textAlignVertical: "center"
        },
        style
      ]}
      {...props}
    >
      {name}
    </RNText>
  );
}
